import { Package, Clock as ClockIcon, Users, Timer, Camera, AlertTriangle, Clock, ImageOff, DollarSign, FileText, ExternalLink } from 'lucide-react';
import { buildAsIsUrl, buildSalePageUrl } from '../../../utils/ikea-urls';

interface DataSourceSummary {
  total_products: number;
  missing_price: number;
  missing_image: number;
  missing_name: number;
  stale_products: number;
  freshness_24h: number;
  freshness_7d: number;
  last_updated: string | null;
}

interface StoreSummaryCardsProps {
  asis: DataSourceSummary;
  lastChance?: DataSourceSummary | null;
  familyOffers?: DataSourceSummary | null;
  limitedTime?: DataSourceSummary | null;
  physical: DataSourceSummary;
  storeId: string;
  country: string;
  slug: string | null;
}

const EMPTY_SUMMARY: DataSourceSummary = {
  total_products: 0,
  missing_price: 0,
  missing_image: 0,
  missing_name: 0,
  stale_products: 0,
  freshness_24h: 0,
  freshness_7d: 0,
  last_updated: null,
};

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function DataSourceCard({
  title,
  icon: Icon,
  iconColor,
  bgColor,
  data,
  externalUrl,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  iconColor: string;
  bgColor: string;
  data: DataSourceSummary;
  externalUrl?: string;
}) {
  const totalIssues = data.missing_price + data.missing_image + data.missing_name + data.stale_products;
  const hasIssues = totalIssues > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 ${bgColor} rounded-lg`}>
            <Icon className={iconColor} size={20} />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{title}</h3>
            <p className="text-2xl font-semibold">{data.total_products.toLocaleString()}</p>
          </div>
        </div>
        {hasIssues && (
          <div className="flex items-center gap-1 text-amber-600">
            <AlertTriangle size={16} />
            <span className="text-sm font-medium">{totalIssues} issues</span>
          </div>
        )}
      </div>

      {/* Freshness Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 mb-1">Fresh (24h)</p>
          <p className={`text-lg font-semibold ${data.freshness_24h >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
            {data.freshness_24h}%
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 mb-1">Fresh (7d)</p>
          <p className={`text-lg font-semibold ${data.freshness_7d >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
            {data.freshness_7d}%
          </p>
        </div>
      </div>

      {/* Issue Breakdown */}
      <div className="space-y-2 text-sm">
        {data.missing_price > 0 && (
          <div className="flex items-center justify-between text-amber-600">
            <div className="flex items-center gap-1.5">
              <DollarSign size={14} />
              <span>Missing price</span>
            </div>
            <span className="font-medium">{data.missing_price}</span>
          </div>
        )}
        {data.missing_image > 0 && (
          <div className="flex items-center justify-between text-amber-600">
            <div className="flex items-center gap-1.5">
              <ImageOff size={14} />
              <span>Missing image</span>
            </div>
            <span className="font-medium">{data.missing_image}</span>
          </div>
        )}
        {data.missing_name > 0 && (
          <div className="flex items-center justify-between text-amber-600">
            <div className="flex items-center gap-1.5">
              <FileText size={14} />
              <span>Missing name</span>
            </div>
            <span className="font-medium">{data.missing_name}</span>
          </div>
        )}
        {data.stale_products > 0 && (
          <div className="flex items-center justify-between text-red-600">
            <div className="flex items-center gap-1.5">
              <Clock size={14} />
              <span>Stale products</span>
            </div>
            <span className="font-medium">{data.stale_products}</span>
          </div>
        )}
      </div>

      {/* Footer: Last Updated + External Link */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
        <div className="text-xs text-gray-400 flex items-center gap-1">
          <Clock size={12} />
          <span>Updated {formatTimeAgo(data.last_updated)}</span>
        </div>
        {externalUrl && (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            <ExternalLink size={12} />
            View on IKEA
          </a>
        )}
      </div>
    </div>
  );
}

export function StoreSummaryCards({ asis, lastChance, familyOffers, limitedTime, physical, storeId, country, slug }: StoreSummaryCardsProps) {
  const asIsUrl = buildAsIsUrl(storeId, country, slug);
  const lastChanceUrl = buildSalePageUrl(country, 'last_chance');
  const familyOffersUrl = buildSalePageUrl(country, 'family_offers');
  const limitedTimeUrl = buildSalePageUrl(country, 'limited_time');

  // Use EMPTY_SUMMARY for optional data sources that may not exist yet
  const safeLastChance = lastChance || EMPTY_SUMMARY;
  const safeFamilyOffers = familyOffers || EMPTY_SUMMARY;
  const safeLimitedTime = limitedTime || EMPTY_SUMMARY;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <DataSourceCard
        title="As-Is Products"
        icon={Package}
        iconColor="text-blue-600"
        bgColor="bg-blue-100"
        data={asis}
        externalUrl={asIsUrl}
      />
      <DataSourceCard
        title="Last Chance"
        icon={ClockIcon}
        iconColor="text-orange-600"
        bgColor="bg-orange-100"
        data={safeLastChance}
        externalUrl={lastChanceUrl}
      />
      <DataSourceCard
        title="Family Offers"
        icon={Users}
        iconColor="text-green-600"
        bgColor="bg-green-100"
        data={safeFamilyOffers}
        externalUrl={familyOffersUrl}
      />
      <DataSourceCard
        title="Limited Time"
        icon={Timer}
        iconColor="text-red-600"
        bgColor="bg-red-100"
        data={safeLimitedTime}
        externalUrl={limitedTimeUrl}
      />
      <DataSourceCard
        title="Physical Scans"
        icon={Camera}
        iconColor="text-purple-600"
        bgColor="bg-purple-100"
        data={physical}
      />
    </div>
  );
}
